import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── This client's business partners (往来单位) ───────────────────────────────
// The master list is firm-wide (owner's decision 2026-07-31): the same supplier serves many clients,
// and a corrected address must be fixed once, not once per client. This tab is the client's REFERENCE
// list — which partners they actually deal with — so removing one here unlinks it, never deletes the
// master row another client may be using.
//
// Address is a first-class column, not a detail: Form 50 Tawi prints the payee's address, so a
// partner without one silently produces a certificate with a blank address line.

export default function Partners({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [edit, setEdit] = useState<any>(null);       // the row open in the editor (or {} for a new one)

  const load = useCallback(() => {
    api.clientPartners(clientId).then((r: any) => setRows(r.rows || [])).catch(() => setRows([]));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const doImport = async () => {
    setBusy('import');
    try {
      const r: any = await api.partnerImport(clientId);
      load();
      alert(t('pt_import_done').replace('{c}', String(r.created || 0)).replace('{l}', String(r.linked || 0)).replace('{s}', String(r.scanned || 0)));
    } catch (e: any) { alert(e.message); } finally { setBusy(''); }
  };

  // ── VAT-ledger backfill (owner 2026-08-17, firm-wide): preview → confirm → import ──
  // Every distinct 13-digit tax ID from the whole VAT ledger becomes ONE unverified partner named
  // after its most frequent OCR variant. Rows without a valid tax ID are only reported (ruling #5).
  const [bf, setBf] = useState<any>(null);            // preview payload while the modal is open
  const [bfBusy, setBfBusy] = useState(false);
  const openBackfill = async () => {
    setBusy('vat');
    try { setBf(await api.partnerBackfillPreview()); }
    catch (e: any) { alert(e.message); }
    finally { setBusy(''); }
  };
  const runBackfill = async () => {
    if (bfBusy) return;
    setBfBusy(true);
    try {
      const r: any = await api.partnerBackfill();
      setBf(null); load();
      alert(`Imported ${r.created} partner(s) from the VAT ledger (all UNVERIFIED — confirm the correct names). ${r.linked} client link(s) created.${r.skipped ? ` ${r.skipped} already existed.` : ''}`);
    } catch (e: any) { alert(e.message); } finally { setBfBusy(false); }
  };

  // ── Verified-name ledger refresh (owner 2026-08-17): current year only, filed months skipped ──
  // After a human verifies (or renames) a partner, offer to refresh this YEAR's ledger rows for its
  // tax ID to the confirmed name — with the counts shown before anything changes.
  const offerLedgerRefresh = async (partnerId: string) => {
    try {
      const p: any = await api.partnerLedgerRefreshPreview(partnerId);
      if (!p || !p.updatable) return;
      const filedNote = p.filedSkipped ? ` ${p.filedSkipped} row(s) in already-filed months stay unchanged.` : '';
      const otherNote = p.otherYears ? ` ${p.otherYears} row(s) in other years are never touched.` : '';
      if (window.confirm(`Also refresh ${p.updatable} VAT ledger row(s) of ${p.year} (across ${p.clients} client(s)) to "${p.name}"?${filedNote}${otherNote}`)) {
        const r: any = await api.partnerLedgerRefresh(partnerId);
        alert(`Updated ${r.updated} ledger row(s) to "${p.name}".${r.filedSkipped ? ` ${r.filedSkipped} filed-month row(s) skipped.` : ''}`);
      }
    } catch { /* refresh is an offer, never a blocker */ }
  };

  const unlink = async (p: any) => {
    if (!window.confirm(t('pt_unlink_confirm'))) return;
    try { await api.partnerUnlink(clientId, p.id); load(); } catch (e: any) { alert(e.message); }
  };

  const save = async () => {
    const b = edit || {};
    if (!String(b.name || '').trim()) return;
    setBusy('save');
    try {
      if (b.id) { await api.partnerUpdate(b.id, b); setEdit(null); load(); await offerLedgerRefresh(b.id); }
      else { await api.partnerCreate({ ...b, clientId }); setEdit(null); load(); }
    } catch (e: any) { alert(e.message); } finally { setBusy(''); }
  };

  const shown = rows.filter(r => !q.trim()
    || `${r.name} ${r.name_th || ''} ${r.tax_id || ''}`.toLowerCase().includes(q.trim().toLowerCase()));

  // 11 → 12px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
  // Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like
  // this: the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular
  // weight, where they were light grey in bold, 10px above and below where it was 7, on one 16px line, on white, over the
  // darker --border-strong line — a 37px band, as every list's is.
  // 10 → 5px under the heading text (report c151d269: "Bring the heading text 5 pixels closer to the grey line
  // below. Do this for every page." — see Active Jobs' th; the uppercase stays).
  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 10px 5px', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textTransform: 'uppercase', borderBottom: '1px solid var(--border-strong)', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const td: React.CSSProperties = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
  const inp: React.CSSProperties = { width: '100%', padding: '7px 9px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg-app)', color: 'var(--text-primary)', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', margin: '9px 0 3px' };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('pt_search_hint')} style={{ ...inp, width: 260 }} />
        <div style={{ flex: 1 }} />
        <button onClick={() => setEdit({ entityType: 'company', branchNo: '00000' })}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>
          <i className="ti ti-plus" />{t('pt_new')}
        </button>
        {/* The counterparties are already in this client's invoices and WHT lines — this turns them
            into a list that can be picked from, instead of asking anyone to retype them. */}
        <button onClick={doImport} disabled={!!busy}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: 'none', background: 'var(--accent-bg)', color: 'var(--accent-text)', cursor: busy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 700, opacity: busy ? 0.6 : 1 }}>
          <i className={`ti ${busy === 'import' ? 'ti-loader-2' : 'ti-download'}`} style={{ animation: busy === 'import' ? 'spin .8s linear infinite' : undefined, display: 'inline-block' }} />{t('pt_import')}
        </button>
        {/* Firm-wide: seeds the master from EVERY client's VAT ledger, keyed by 13-digit tax ID. */}
        <button onClick={openBackfill} disabled={!!busy}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: busy ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 700, opacity: busy ? 0.6 : 1 }}>
          <i className={`ti ${busy === 'vat' ? 'ti-loader-2' : 'ti-database-import'}`} style={{ animation: busy === 'vat' ? 'spin .8s linear infinite' : undefined, display: 'inline-block' }} />Import from VAT
        </button>
      </div>

      {!rows.length ? (
        <div style={{ padding: '34px 16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('pt_empty')}</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <thead><tr>
            <th style={th}>{t('ct_name')}</th>
            <th style={th}>{t('ct_tax_id')}</th>
            <th style={th}>{t('ct_address')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pt_used')}</th>
            <th style={th} />
          </tr></thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.id}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{r.name}</div>
                  {r.name_th && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.name_th}</div>}
                  <span style={{ fontSize: 10.5, padding: '1px 6px', borderRadius: 5, background: r.entity_type === 'individual' ? '#FEF3C7' : 'var(--accent-bg)', color: r.entity_type === 'individual' ? '#B45309' : 'var(--accent-text)' }}>
                    {r.entity_type === 'individual' ? t('wht_individual') : t('wht_company')}
                  </span>
                  {/* Web-lookup suggestions arrive unverified and never override anything until a
                      human confirms them — this is that confirmation. */}
                  {!r.verified && (
                    <span style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span title={t('pt_unverified_tip')} style={{ fontSize: 10.5, padding: '1px 6px', borderRadius: 5, background: '#FEF3F2', color: '#B42318', fontWeight: 700 }}>
                        <i className="ti ti-alert-triangle" style={{ marginRight: 3 }} />{t('pt_unverified')}
                      </span>
                      <button onClick={async () => { try { await api.partnerUpdate(r.id, { verified: true }); load(); await offerLedgerRefresh(r.id); } catch (e: any) { alert(e.message); } }}
                        style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 8px', borderRadius: 5, border: '1px solid #15803D', background: '#ECFDF5', color: '#15803D', cursor: 'pointer' }}>
                        <i className="ti ti-check" style={{ marginRight: 2 }} />{t('pt_verify')}
                      </button>
                    </span>
                  )}
                </td>
                <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{r.tax_id || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}</td>
                <td style={td}>
                  {String(r.address || '').trim()
                    ? <span style={{ color: 'var(--text-secondary)' }}>{r.address}</span>
                    : <span title={t('pt_no_address_tip')} style={{ color: '#B45309', fontWeight: 600 }}><i className="ti ti-alert-triangle" style={{ marginRight: 4 }} />{t('pt_no_address')}</span>}
                </td>
                <td style={{ ...td, textAlign: 'right', color: 'var(--text-secondary)' }}>{r.used || 0}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button onClick={() => setEdit({ id: r.id, name: r.name, nameTh: r.name_th, taxId: r.tax_id, branchNo: r.branch_no, entityType: r.entity_type, address: r.address, phone: r.phone, email: r.email, note: r.note })}
                    title={t('ct_edit')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}><i className="ti ti-pencil" /></button>
                  <button onClick={() => unlink(r)} title={t('pt_unlink')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-unlink" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {bf && (
        <div onClick={e => { if (e.target === e.currentTarget && !bfBusy) setBf(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 20 }}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 12, width: 'min(760px, 100%)', maxHeight: '88vh', overflow: 'auto', padding: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Import partners from the VAT ledger (firm-wide)</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              One partner per 13-digit Tax ID, named after its most frequent OCR variant, imported as <b>UNVERIFIED</b> —
              nothing overrides the ledgers until a person verifies the correct name.
            </div>
            <div style={{ fontSize: 12, marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span><b>{(bf.candidates || []).length}</b> tax ID(s) to import</span>
              <span style={{ color: 'var(--text-tertiary)' }}><b>{bf.alreadyInPartners || 0}</b> already in the database</span>
              {bf.noTaxId?.rows > 0 && (
                <span style={{ color: '#B45309', fontWeight: 600 }}>
                  ⚠ {bf.noTaxId.rows} ledger row(s) (across {bf.noTaxId.clients} client(s)) have NO valid 13-digit tax ID — cannot be keyed, not imported.
                </span>
              )}
              {(bf.blankName || []).length > 0 && (
                <span style={{ color: '#B45309', fontWeight: 600 }}>
                  ⚠ {(bf.blankName || []).length} tax ID(s) have a blank name on every row — nothing usable to import: {(bf.blankName || []).map((x: any) => x.taxId).join(', ')}
                </span>
              )}
            </div>
            {(bf.candidates || []).length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10, background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                <thead><tr>
                  <th style={th}>Tax ID</th><th style={th}>Proposed name (most frequent)</th>
                  <th style={{ ...th, textAlign: 'right' }}>Variants</th><th style={{ ...th, textAlign: 'right' }}>Rows</th><th style={{ ...th, textAlign: 'right' }}>Clients</th>
                </tr></thead>
                <tbody>
                  {(bf.candidates || []).map((c: any) => (
                    <tr key={c.taxId}>
                      <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{c.taxId}</td>
                      <td style={td}>{c.name}{c.variants > 1 && <span style={{ marginLeft: 6, fontSize: 10.5, padding: '1px 6px', borderRadius: 5, background: '#FEF3F2', color: '#B42318', fontWeight: 700 }}>{c.variants} variants</span>}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{c.variants}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{c.rows}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{c.clients}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button disabled={bfBusy} onClick={() => setBf(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>{t('cancel')}</button>
              <button disabled={bfBusy || !(bf.candidates || []).length} onClick={runBackfill}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: bfBusy ? 0.7 : 1 }}>
                {bfBusy ? <i className="ti ti-loader-2" style={{ animation: 'spin .8s linear infinite', display: 'inline-block' }} /> : `Import ${(bf.candidates || []).length} partner(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {edit && (
        <div onClick={e => { if (e.target === e.currentTarget) setEdit(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 12, width: 'min(560px, 100%)', maxHeight: '88vh', overflow: 'auto', padding: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>{edit.id ? t('ct_edit') : t('pt_new')}</div>
            <label style={lbl}>{t('ct_name')}</label>
            <input value={edit.name || ''} onChange={e => setEdit({ ...edit, name: e.target.value })} style={inp} />
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>{t('ct_tax_id')}</label>
                <input value={edit.taxId || ''} onChange={e => setEdit({ ...edit, taxId: e.target.value })} style={inp} />
              </div>
              <div style={{ width: 130 }}>
                <label style={lbl}>{t('ct_branch')}</label>
                <input value={edit.branchNo || ''} onChange={e => setEdit({ ...edit, branchNo: e.target.value })} style={inp} />
              </div>
              <div style={{ width: 150 }}>
                <label style={lbl}>{t('wht_type')}</label>
                <select value={edit.entityType || 'company'} onChange={e => setEdit({ ...edit, entityType: e.target.value })} style={inp}>
                  <option value="company">{t('wht_company')}</option>
                  <option value="individual">{t('wht_individual')}</option>
                </select>
              </div>
            </div>
            <label style={lbl}>{t('ct_address')}</label>
            <textarea value={edit.address || ''} onChange={e => setEdit({ ...edit, address: e.target.value })} rows={2} style={{ ...inp, resize: 'vertical' }} />
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('pt_no_address_tip')}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button onClick={() => setEdit(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>{t('cancel')}</button>
              <button onClick={save} disabled={busy === 'save' || !String(edit.name || '').trim()}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: busy === 'save' ? 0.7 : 1 }}>
                {busy === 'save' ? <i className="ti ti-loader-2" style={{ animation: 'spin .8s linear infinite', display: 'inline-block' }} /> : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
